// When no logo is detected, display this helper

import { useEffect, useState } from "react";
import {
  Case,
  Default,
  Switch,
  When,
} from "react-if";

import { Card } from "../../../../common/Atoms/Card";
import { useApiRequest } from "../../../hooks/useApiRequest";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { env } from "../../../../common/lib/env";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { CardContent } from "../../../../common/Atoms/CardContent";
import { Button } from "../../../../common/Atoms/Button";
import { WrappedLink } from "../../WrappedLink";
import { Notification } from "../../../../common/Atoms/Notification";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";

export function LogoHelper() {
  const currentOrg = useGetSelectedOrganisation();
  const apiRequest = useApiRequest();
  const [suggestedLogoUrl, setSuggestedLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [useLogoLoading, setUseLogoLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logoUsedSuccess, setLogoUsedSuccess] = useState<boolean>(false);

  useEffect(() => {
    const fetchSuggestedLogo = async() => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiRequest({
          method: `GET`,
          url: `${env.baseApiUrl}organisation/suggestedLogo`,
        });
        setSuggestedLogoUrl(response.data.logoUrl);
      }
      catch (err) {
        if (err.status === 404) {
          setError(`No suggested logo found for your organisation.`);
        }
        else {
          setError(`Failed to fetch suggested logo: ${err.message}`);
        }
      }

      setLoading(false);
    };

    if (!suggestedLogoUrl && currentOrg) {
      fetchSuggestedLogo();
    }
  }, [suggestedLogoUrl, currentOrg]);

  async function onUseLogo() {
    setUseLogoLoading(true);

    await apiRequest({
      method: `POST`,
      url: `${env.baseApiUrl}organisation/useSuggestedLogo`,
    });

    setLogoUsedSuccess(true);
    setUseLogoLoading(false);
  }

  return (
    <LoadingOverlay
      loading={ loading }
    >
      <Card className={ `min-h-full min-w-full` }>
        <CardContent>
          <Switch>
            <Case condition={ logoUsedSuccess }>
              <Notification
                type={ `success` }
                className={ `mb-4` }
              >
                { `Logo successfully updated!` }
              </Notification>
            </Case>

            <Default>
              <Heading
                variant={ `secondary` }
              >
                { `Is this your logo?` }
              </Heading>
              <Paragraph
                variant={ `help` }
              >
                { `We will use your logo in emails, statements and the customer facing portal.` }
              </Paragraph>

              <When condition={ suggestedLogoUrl }>
                <div className={ `mt-8` }>
                  <div className={ `w-full flex items-center justify-center` }>
                    <img
                      src={ suggestedLogoUrl }
                      className={ `App-logo` }
                      alt={ `logo` }
                    />
                  </div>

                  <div className={ `flex justify-center` }>
                    <div className={ `space-x-2 mt-6` }>
                      <Button
                        color={ `paidniceGreenOutline` }
                        onClick={ onUseLogo }
                        loading={ useLogoLoading }
                        disabled={ useLogoLoading }
                      >
                        { `Use this logo` }
                      </Button>
                      <WrappedLink
                        to={ `/settings/general` }
                      >
                        <Button
                          color={ `orangeOutline` }
                        >
                          { `Upload a different logo` }
                        </Button>
                      </WrappedLink>
                    </div>
                  </div>
                </div>
              </When>
            </Default>
          </Switch>

          <When condition={ error }>
            <Notification
              type={ `error` }
              className={ `mt-4` }
            >
              { error }
            </Notification>
          </When>
        </CardContent>
      </Card>
    </LoadingOverlay>
  );
}
